import React, { useState } from "react";
import { X, Plus, Trash, FloppyDisk } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

const PALETTE = [
  { color: "#FF3B30", label: "Rojo" },
  { color: "#32D74B", label: "Verde" },
  { color: "#FFD60A", label: "Amarillo" },
  { color: "#007AFF", label: "Azul" },
  { color: "#FF9500", label: "Naranja" },
  { color: "#5E5CE6", label: "Violeta" },
  { color: "#FFFFFF", label: "Blanco" },
  { color: "#000000", label: "Negro" },
];

export default function ColorLightsConfigEditor({ existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [form, setForm] = useState(() => existing ? {
    name: existing.name || "",
    description: existing.description || "",
    sequence: existing.sequence?.length ? existing.sequence : [{ color: "#FF3B30", duration_ms: 1000, label: "Rojo" }],
    repeats: existing.repeats || 1,
    target_color: existing.target_color || "",
    target_label: existing.target_label || "",
    rest_ms_between_repeats: existing.rest_ms_between_repeats || 0,
    randomize: existing.randomize !== false, // default true; legacy configs sin campo → true
    target_probability: typeof existing.target_probability === "number" ? existing.target_probability : 0.3,
  } : {
    name: "",
    description: "",
    sequence: [
      { color: "#FF3B30", duration_ms: 1000, label: "Rojo" },
      { color: "#32D74B", duration_ms: 1000, label: "Verde" },
      { color: "#FFD60A", duration_ms: 1000, label: "Amarillo" },
    ],
    repeats: 3,
    target_color: "",
    target_label: "",
    rest_ms_between_repeats: 500,
    randomize: true,
    target_probability: 0.3,
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateStep = (idx, patch) => {
    setForm((f) => ({ ...f, sequence: f.sequence.map((s, i) => i === idx ? { ...s, ...patch } : s) }));
  };
  const addStep = () => {
    setForm((f) => ({ ...f, sequence: [...f.sequence, { color: "#FF3B30", duration_ms: 1000, label: "Rojo" }] }));
  };
  const removeStep = (idx) => {
    setForm((f) => ({ ...f, sequence: f.sequence.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Ponele un nombre a la configuración");
      return;
    }
    if (!form.sequence.length) {
      toast.error("Agregá al menos un paso");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        game_type: "color_lights",
        repeats: Number(form.repeats) || 1,
        rest_ms_between_repeats: Number(form.rest_ms_between_repeats) || 0,
        randomize: !!form.randomize,
        target_probability: Math.min(1, Math.max(0, Number(form.target_probability) || 0)),
        sequence: form.sequence.map((s) => ({
          color: s.color,
          duration_ms: Number(s.duration_ms) || 1000,
          label: s.label || null,
        })),
        target_color: form.target_color || null,
        target_label: form.target_label || null,
      };
      if (isEdit) {
        await api.put(`/neuroscience/configs/${existing.config_id}`, payload);
        toast.success("Configuración actualizada");
      } else {
        await api.post("/neuroscience/configs", payload);
        toast.success("Configuración creada");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="color-lights-editor">
      <div className="card-base p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-black uppercase text-xl tracking-tighter">
            {isEdit ? "Editar configuración" : "Nueva configuración"}
          </h2>
          <button onClick={onClose} className="text-muted hover:text-white" data-testid="editor-close">
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted block mb-2">Nombre *</label>
            <input className="input-base" value={form.name} onChange={(e) => update({ name: e.target.value })} placeholder="Ej: Reacción visual básica" data-testid="cfg-name" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted block mb-2">Descripción (opcional)</label>
            <input className="input-base" value={form.description} onChange={(e) => update({ description: e.target.value })} placeholder="Para qué sirve este drill" data-testid="cfg-description" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-muted block mb-2">
              {form.randomize ? "Colores (se muestran en orden aleatorio)" : "Secuencia de colores"}
            </label>
            <div className="space-y-2" data-testid="cfg-sequence">
              {form.sequence.map((s, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-app p-3 flex flex-wrap items-center gap-2" data-testid={`step-${i}`}>
                  <span className="text-[10px] uppercase tracking-widest text-muted w-6">{i + 1}</span>
                  <div className="flex flex-wrap gap-1">
                    {PALETTE.map((p) => (
                      <button
                        key={p.color}
                        type="button"
                        onClick={() => updateStep(i, { color: p.color, label: p.label })}
                        className={`w-7 h-7 border-2 transition-all ${s.color === p.color ? "border-white scale-110" : "border-[#262626]"}`}
                        style={{ backgroundColor: p.color }}
                        title={p.label}
                        data-testid={`step-${i}-color-${p.label}`}
                      />
                    ))}
                    <label
                      className={`w-7 h-7 border-2 cursor-pointer relative overflow-hidden flex items-center justify-center ${!PALETTE.some(p => p.color.toLowerCase() === (s.color || "").toLowerCase()) ? "border-white scale-110" : "border-[#262626]"}`}
                      style={{ backgroundColor: !PALETTE.some(p => p.color.toLowerCase() === (s.color || "").toLowerCase()) ? s.color : "transparent", backgroundImage: PALETTE.some(p => p.color.toLowerCase() === (s.color || "").toLowerCase()) ? "linear-gradient(135deg, #FF3B30 0%, #FFD60A 25%, #32D74B 50%, #007AFF 75%, #5E5CE6 100%)" : "none" }}
                      title="Color personalizado"
                      data-testid={`step-${i}-color-custom`}
                    >
                      <input
                        type="color"
                        value={s.color || "#FF3B30"}
                        onChange={(e) => updateStep(i, { color: e.target.value, label: "Custom" })}
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                      />
                    </label>
                  </div>
                  <input
                    type="number" min="100" step="100"
                    className="input-base !py-1.5 !w-24 text-xs"
                    value={s.duration_ms}
                    onChange={(e) => updateStep(i, { duration_ms: Number(e.target.value) })}
                    title="Duración en milisegundos"
                    data-testid={`step-${i}-duration`}
                  />
                  <span className="text-[10px] text-muted">ms</span>
                  <button onClick={() => removeStep(i)} className="btn-secondary !py-1 !px-2 text-xs hover:!border-[#FF3B30] hover:text-[#FF3B30] ml-auto" data-testid={`step-${i}-remove`}>
                    <Trash size={12} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={addStep} className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-2 mt-2" data-testid="add-step">
              <Plus size={12} weight="bold" /> Agregar paso
            </button>
          </div>

          <label className="flex items-start gap-3 cursor-pointer bg-[#0A0A0A] border border-app p-3" data-testid="cfg-randomize-wrap">
            <input
              type="checkbox"
              checked={!!form.randomize}
              onChange={(e) => update({ randomize: e.target.checked })}
              className="mt-0.5 w-4 h-4 accent-[#FF3B30] flex-shrink-0"
              data-testid="cfg-randomize"
            />
            <div>
              <div className="text-sm font-semibold">Orden aleatorio</div>
              <div className="text-[11px] text-muted leading-relaxed mt-0.5">
                Los colores que elegiste se muestran en orden distinto cada vez que el atleta juega (y en cada repetición). Si lo desactivás, se reproducen siempre en el orden de la lista.
              </div>
            </div>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted block mb-2">Repeticiones</label>
              <input type="number" min="1" max="50" className="input-base" value={form.repeats} onChange={(e) => update({ repeats: e.target.value })} data-testid="cfg-repeats" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted block mb-2">Pausa entre reps (ms)</label>
              <input type="number" min="0" step="100" className="input-base" value={form.rest_ms_between_repeats} onChange={(e) => update({ rest_ms_between_repeats: e.target.value })} data-testid="cfg-rest" />
            </div>
          </div>

          <div className="bg-[#0A0A0A] border border-app p-3">
            <div className="text-xs uppercase tracking-widest text-muted mb-2">Color objetivo (opcional)</div>
            <p className="text-[11px] text-muted mb-3 leading-relaxed">
              Si elegís un color, el atleta deberá tocar la pantalla cada vez que aparezca. Se medirá su tiempo de reacción.
            </p>
            <div className="flex flex-wrap gap-1 mb-3">
              <button onClick={() => update({ target_color: "", target_label: "" })} className={`px-2 py-1 text-[10px] uppercase border ${!form.target_color ? "border-white text-white" : "border-app text-muted"}`} data-testid="target-none">
                Sin target
              </button>
              {PALETTE.map((p) => (
                <button
                  key={p.color}
                  onClick={() => update({ target_color: p.color, target_label: `Tocá cuando veas ${p.label.toLowerCase()}` })}
                  className={`w-7 h-7 border-2 ${form.target_color === p.color ? "border-white scale-110" : "border-[#262626]"}`}
                  style={{ backgroundColor: p.color }}
                  title={p.label}
                  data-testid={`target-${p.label}`}
                />
              ))}
              <label
                className={`w-7 h-7 border-2 cursor-pointer relative overflow-hidden flex items-center justify-center ${form.target_color && !PALETTE.some(p => p.color.toLowerCase() === form.target_color.toLowerCase()) ? "border-white scale-110" : "border-[#262626]"}`}
                style={{
                  backgroundColor: form.target_color && !PALETTE.some(p => p.color.toLowerCase() === form.target_color.toLowerCase()) ? form.target_color : "transparent",
                  backgroundImage: !form.target_color || PALETTE.some(p => p.color.toLowerCase() === form.target_color.toLowerCase()) ? "linear-gradient(135deg, #FF3B30 0%, #FFD60A 25%, #32D74B 50%, #007AFF 75%, #5E5CE6 100%)" : "none",
                }}
                title="Color personalizado"
                data-testid="target-custom"
              >
                <input
                  type="color"
                  value={form.target_color || "#FF3B30"}
                  onChange={(e) => update({ target_color: e.target.value, target_label: form.target_label || "Tocá cuando veas el color" })}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                />
              </label>
            </div>
            {form.target_color && (
              <>
                <input className="input-base text-xs mb-3" value={form.target_label} onChange={(e) => update({ target_label: e.target.value })} placeholder="Instrucción para el atleta" data-testid="cfg-target-label" />
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs uppercase tracking-widest text-muted">% de aparición del target</label>
                    <span className="text-sm font-bold text-primary-app" data-testid="target-prob-value">{Math.round((Number(form.target_probability) || 0) * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={form.target_probability}
                    onChange={(e) => update({ target_probability: Number(e.target.value) })}
                    className="w-full accent-[#FF3B30]"
                    data-testid="cfg-target-probability"
                  />
                  <div className="text-[10px] text-muted mt-1 leading-relaxed">
                    Más alto = el color objetivo aparece más seguido (juego más fácil). Más bajo = exige atención sostenida (más exigente). Solo aplica con orden aleatorio.
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="btn-secondary" data-testid="editor-cancel">Cancelar</button>
            <button onClick={save} disabled={submitting} className="btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="editor-save">
              <FloppyDisk size={16} weight="bold" /> {submitting ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
